import clsx from 'clsx';
import * as React from 'react';

import { CardCheck, CardRadio } from '../Card';
import { Description } from '../Description';
import { Label } from '../Form';
import { ProgressiveImage } from '../Image';
import { RadioGroup } from '../Radio';

/**
 * Configuration for a single tile option in the TilePicker component.
 *
 * @template T - The type of the value associated with this tile option
 */
export type TileOption<T> = {
    /** The value that will be returned when this tile is selected. */
    value: T;
    /** The primary label displayed below the tile image. */
    label: string;
    /** Optional description text displayed below the label. Can include React nodes for rich content. */
    description?: React.ReactNode;
    /**
     * Image assets for the tile. Must provide both light and dark theme variants.
     * The images will be displayed with a 120:78 aspect ratio.
     */
    asset: {
        /** Image URL for light theme */
        light: string;
        /** Image URL for dark theme */
        dark: string;
    };
    /**
     * Optional trailing content displayed next to the label.
     * Useful for badges, icons, or other UI elements like "New", "Premium", etc.
     */
    trailing?: React.ReactNode;
    /**
     * If true, the tile will be disabled.
     */
    disabled?: boolean;
};

export type TilePickerProps<T> = {
    /** The list of options to display. */
    items: TileOption<T>[];
    /** The currently selected item. */
    selectedItem: TileOption<T>['value'];
    /** A callback to set the selected item. */
    setSelectedItem: (value: TileOption<T>['value']) => void;
    /** Additional CSS classes to apply to the component. */
    className?: string;
    /** Whether the picker is disabled. */
    disabled?: boolean;
};

/**
 * TilePicker displays a list of options as tiles, allowing the user to select a single item by clicking on it.
 *
 * It uses a RadioGroup to handle the selection of its `items`. Each `TileOption` contains a `label`, `description`, and light and dark assets. A `trailing` property can be used to add additional content to the tile, like a badge.
 *
 * @see TilePickerProps - The type definition for the component props
 * @see TileOption - The type definition for individual tile configurations
 */
export function TilePicker<T>(props: TilePickerProps<T>): React.ReactElement {
    const { items, selectedItem, setSelectedItem, className, disabled = false, ...rest } = props;

    // Keep a map of a generic enum value to a string since the radio group handles only strings
    const enumValueMap = React.useMemo(() => {
        const enumsMap = new Map<string, T>();
        items.forEach((item) => {
            enumsMap.set(String(item.value), item.value);
        });
        return enumsMap;
    }, [items]);

    return (
        <RadioGroup
            isDisabled={disabled}
            value={String(selectedItem)}
            onChange={(value) => {
                const actualValue = enumValueMap.get(value);
                if (!actualValue) {
                    throw Error(`Invalid value for TilePicker: ${value}`);
                }

                setSelectedItem(actualValue);
            }}
            orientation="horizontal"
            className={className}
            {...rest}
        >
            {items.map((item) => (
                <div className="flex w-full flex-col gap-2" key={String(item.value)}>
                    <CardRadio value={String(item.value)} isDisabled={item.disabled}>
                        <ProgressiveImage
                            src={item.asset}
                            className="size-full object-cover"
                            style={{ aspectRatio: '120/78' }}
                        />
                        <CardCheck
                            className={clsx(
                                'absolute top-3 right-3',
                                selectedItem !== item.value && 'bg-base'
                            )}
                        />
                    </CardRadio>
                    <div className="flex flex-col pl-1">
                        <Label className="flex items-center gap-2">
                            {item.label}
                            {item.trailing}
                        </Label>
                        {item.description ? <Description>{item.description}</Description> : null}
                    </div>
                </div>
            ))}
        </RadioGroup>
    );
}
